import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAccountSettings } from "../../api/settingsApi";
import {
  resendEmailChangeVerification,
  cancelEmailChange,
} from "../../api/authApi";

const DISMISS_KEY = "pendingEmailBannerDismissed";

const PendingEmailBanner = () => {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "true"
  );
  const [resendSuccess, setResendSuccess] = useState(false);
  const queryClient = useQueryClient();

  const { data: settingsData } = useQuery({
    queryKey: ["accountSettings"],
    queryFn: getAccountSettings,
    staleTime: 5 * 60 * 1000,
  });

  const { mutate: resend, isPending: isResending } = useMutation({
    mutationFn: resendEmailChangeVerification,
    onSuccess: () => {
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    },
  });

  const { mutate: cancel, isPending: isCancelling } = useMutation({
    mutationFn: cancelEmailChange,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accountSettings"] });
    },
  });

  const pendingEmail = settingsData?.user?.pendingEmail;

  if (!pendingEmail || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  };

  return (
    <div className="bg-signal-amber-50 border-b border-signal-amber-100">
      <div className="px-4 py-2.5 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {/* Message */}
          <p className="text-sm text-signal-amber-600">
            Email change pending — check{" "}
            <span className="font-semibold">{pendingEmail}</span> for a
            verification link.
          </p>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {resendSuccess ? (
              <span className="text-xs text-accent-700 font-medium">
                Sent!
              </span>
            ) : (
              <button
                type="button"
                onClick={() => resend()}
                disabled={isResending}
                className="text-xs font-medium text-signal-amber-600 hover:text-amber-900 underline disabled:opacity-50"
              >
                {isResending ? "Sending..." : "Resend"}
              </button>
            )}
            <span className="text-amber-300">|</span>
            <button
              type="button"
              onClick={() => cancel()}
              disabled={isCancelling}
              className="text-xs font-medium text-signal-amber-600 hover:text-amber-900 underline disabled:opacity-50"
            >
              {isCancelling ? "Cancelling..." : "Cancel change"}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="ml-1 p-1 text-signal-amber-500 hover:text-signal-amber-600 rounded"
              aria-label="Dismiss"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingEmailBanner;
